// The content of the add field as text and tag chips. A chip is a tag the user picked from the suggestion list; it is one
// element in the editor (`components/todos/QuickAdd.vue`) and a hashtag in the summary that is added. No DOM in here, so the
// editing rules can be tested:
//
// - the "logic text" counts a chip as one space, so `hashtagAt` and the caret positions of the editor use the same indexes,
//   and a chip is a boundary for hashtags like whitespace;
// - picking replaces the open hashtag by a chip, tapping a chip makes it text again with the name selected, Backspace in an
//   empty tag (`#|`, `#"|"`, `#[|]`) removes the whole tag.

import { formatHashtag } from './hashtags'
import { hashtagAt, type Hashtag } from './tags'

export type Segment = { text: string } | { chip: string }

function isChip(segment: Segment): segment is { chip: string } {
  return 'chip' in segment
}

// Neighbouring texts merged, empty texts dropped.
export function normalize(segments: Segment[]): Segment[] {
  const result: Segment[] = []
  for (const segment of segments) {
    if (isChip(segment)) result.push(segment)
    else if (segment.text) {
      const last = result[result.length - 1]
      if (last && !isChip(last)) last.text += segment.text
      else result.push({ text: segment.text })
    }
  }
  return result
}

export function logicText(segments: Segment[]): string {
  return segments.map((segment) => (isChip(segment) ? ' ' : segment.text)).join('')
}

// The summary text: every chip as a hashtag in the form `formatHashtag` picks, with a space between it and its neighbours.
export function plainText(segments: Segment[]): string {
  let out = ''
  segments.forEach((segment, index) => {
    if (!isChip(segment)) {
      out += segment.text
      return
    }
    if (out && !/\s$/.test(out)) out += ' '
    out += formatHashtag(segment.chip)
    const next = segments[index + 1]
    if (next && (isChip(next) || !/^\s/.test(next.text))) out += ' '
  })
  return out
}

// The segments cut in two at a logic index.
function splitAt(segments: Segment[], index: number): [Segment[], Segment[]] {
  const head: Segment[] = []
  const tail: Segment[] = []
  let position = 0
  for (const segment of segments) {
    const length = isChip(segment) ? 1 : segment.text.length
    if (position + length <= index) head.push(segment)
    else if (position >= index) tail.push(segment)
    else if (!isChip(segment)) {
      head.push({ text: segment.text.slice(0, index - position) })
      tail.push({ text: segment.text.slice(index - position) })
    }
    position += length
  }
  return [head, tail]
}

export function replaceRange(
  segments: Segment[],
  start: number,
  end: number,
  insert: Segment[],
): Segment[] {
  const [head] = splitAt(segments, start)
  const [, tail] = splitAt(segments, end)
  return normalize([...head, ...insert, ...tail])
}

// The open hashtag replaced by a chip and a space (none added if one follows), and where the caret belongs afterwards.
export function pickTag(
  segments: Segment[],
  hashtag: Pick<Hashtag, 'start' | 'end'>,
  name: string,
): { segments: Segment[]; caret: number } {
  const [, tail] = splitAt(segments, hashtag.end)
  const needsSpace = !logicText(tail).startsWith(' ')
  return {
    segments: replaceRange(segments, hashtag.start, hashtag.end, [
      { chip: name },
      ...(needsSpace ? [{ text: ' ' }] : []),
    ]),
    caret: hashtag.start + 2,
  }
}

// A chip made text again: its hashtag, with the name (inside the quotes or brackets, if it has them) selected. `index` is
// the chip's logic index; null if there is no chip there.
export function revertChip(
  segments: Segment[],
  index: number,
): { segments: Segment[]; selection: [number, number] } | null {
  const [head, tail] = splitAt(segments, index)
  const chip = tail[0]
  if (!chip || !isChip(chip) || logicText(head).length !== index) return null
  const tag = formatHashtag(chip.chip)
  const quoted = tag.startsWith('#"') || tag.startsWith('#[')
  return {
    segments: normalize([...head, { text: tag }, ...tail.slice(1)]),
    selection: [index + (quoted ? 2 : 1), index + tag.length - (quoted ? 1 : 0)],
  }
}

// Backspace in an empty tag takes the whole tag with it: `#|`, `#"|"`, `#[|]` (and an unfinished `#"|`, `#[|`). None when
// the caret is not right behind the opening of an empty hashtag.
export function deleteEmptyHashtag(
  segments: Segment[],
  caret: number,
): { segments: Segment[]; caret: number } | null {
  const logic = logicText(segments)
  const hashtag = hashtagAt(logic, caret)
  if (!hashtag || hashtag.query !== '' || hashtag.full !== '') return null
  const opening = logic[hashtag.start + 1] === '"' || logic[hashtag.start + 1] === '[' ? 2 : 1
  if (caret !== hashtag.start + opening) return null
  return {
    segments: replaceRange(segments, hashtag.start, hashtag.end, []),
    caret: hashtag.start,
  }
}
